import {
  BaseChartOptions,
  HighchartsOptions,
} from 'demand-forecasting/types/forecast-chart/highcharts-options';
import {
  ChartOptions,
  ColorAxisOptions,
  ExportingOptions,
  LegendOptions,
  NavigatorOptions,
  PlotOptions,
  RangeSelectorOptions,
  SeriesOptionsType,
  TooltipOptions,
  XAxisOptions,
  YAxisOptions,
} from 'highcharts';
import { merge } from 'lodash-es';

export class ChartOptionsBuilder {
  private readonly chartOptions: HighchartsOptions;

  /**
   * @constructor
   * @param {HighchartsOptions} defaultOptions (optional) - try to avoid passing this argument
   * and instead use the builder. The default values should only come from the empty chartOptions.
   * */
  constructor(defaultOptions?: HighchartsOptions) {
    if (defaultOptions) {
      this.chartOptions = defaultOptions;
    } else {
      this.chartOptions = BaseChartOptions.getEmptyChartOptions();
    }
  }

  public withXAxis(xAxis: XAxisOptions): ChartOptionsBuilder {
    this.chartOptions.xAxis = merge(this.chartOptions.xAxis, xAxis);
    return this;
  }

  public withYAxis(yAxis: YAxisOptions): ChartOptionsBuilder {
    this.chartOptions.yAxis = merge(this.chartOptions.yAxis, yAxis);
    return this;
  }

  public withNavigator(navigator: NavigatorOptions): ChartOptionsBuilder {
    this.chartOptions.navigator = merge(this.chartOptions.navigator, navigator);
    return this;
  }

  public withPlotOptions(plotOptions: PlotOptions): ChartOptionsBuilder {
    this.chartOptions.plotOptions = merge(
      this.chartOptions.plotOptions ?? {},
      plotOptions,
    );
    return this;
  }

  public withChartOptions(chartOptions: ChartOptions): ChartOptionsBuilder {
    this.chartOptions.chart = merge(
      this.chartOptions.chart ?? {},
      chartOptions,
    );
    return this;
  }

  public withExportingOptions(
    exportingOptions: ExportingOptions,
  ): ChartOptionsBuilder {
    this.chartOptions.exporting = merge(
      this.chartOptions.exporting ?? {},
      exportingOptions,
    );
    return this;
  }

  public withSeries(
    series: SeriesOptionsType | SeriesOptionsType[],
  ): ChartOptionsBuilder {
    const seriesAsArray = Array.isArray(series) ? series : [series];
    this.chartOptions.series.push(...seriesAsArray);
    return this;
  }

  public withRangeSelector(
    rangeSelector: RangeSelectorOptions,
  ): ChartOptionsBuilder {
    this.chartOptions.rangeSelector = merge(
      this.chartOptions.rangeSelector,
      rangeSelector,
    );
    return this;
  }

  public withLegend(legend: LegendOptions) {
    this.chartOptions.legend = merge(this.chartOptions.legend ?? {}, legend);
    return this;
  }

  public withColorAxis(colorAxisOptions: ColorAxisOptions) {
    this.chartOptions.colorAxis = merge(
      this.chartOptions.colorAxis ?? {},
      colorAxisOptions,
    );
    return this;
  }

  public withTooltip(tooltip: TooltipOptions): ChartOptionsBuilder {
    this.chartOptions.tooltip = merge(this.chartOptions.tooltip ?? {}, tooltip);
    return this;
  }

  /**
   * @returns {HighchartsOptions} the composed options
   */
  public build(): HighchartsOptions {
    // here we freeze to object to make it immutable, if you see this and have problems ask @Josua
    // Object.freeze(this.chartOptions);
    // freeze causes a weird cannot delete navigator of object error on the main chart -- id say that's a bad highcharts implementation LOL
    return this.chartOptions;
  }
}
